import React, { useState, useEffect, useRef } from 'react';
import './AddressAutocomplete.css';

function AddressAutocomplete({
  value = '',
  onChange,
  onSelect,
  placeholder = 'Search address…',
  name = 'address_line_1',
  label = 'Address Line 1',
  disabled = false,
}) {
  const [query, setQuery] = useState(value);
  const [location, setLocation] = useState(null);
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Sync external value changes
  useEffect(() => { setQuery(value); }, [value]);

  const onSelectRef = useRef(onSelect);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onSelectRef.current = onSelect;
    onChangeRef.current = onChange;
  }, [onSelect, onChange]);

  useEffect(() => {
    let checkInterval;
    let isInitialized = false;

    const loadGoogleMapsScript = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        return Promise.resolve();
      }

      const scriptId = 'google-maps-script';
      if (document.getElementById(scriptId)) {
        return new Promise((resolve) => {
          checkInterval = setInterval(() => {
            if (window.google && window.google.maps && window.google.maps.places) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
        });
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyB-SolJ-RsV17WmuJb5OJ2g44LDOnsrXCU&libraries=places,maps`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    };

    const initAutocomplete = () => {
      if (isInitialized) return;
      if (window.google && window.google.maps && window.google.maps.places && inputRef.current) {
        autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
          fields: ['address_components', 'geometry', 'formatted_address', 'name'],
        });

        autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current.getPlace();
          
          if (!place.geometry) {
            return;
          }

          let address_line_1 = '';
          let city = '';
          let state = '';
          let country = '';
          let zip_code = '';

          const components = place.address_components || [];

          const streetNumber = components.find(c => c.types.includes('street_number'))?.long_name || '';
          const route = components.find(c => c.types.includes('route'))?.long_name || '';
          address_line_1 = `${streetNumber} ${route}`.trim();
          if (!address_line_1) address_line_1 = place.name || ''; // Fallback

          city = components.find(c => c.types.includes('locality') || c.types.includes('postal_town'))?.long_name || '';
          state = components.find(c => c.types.includes('administrative_area_level_1'))?.long_name || '';
          country = components.find(c => c.types.includes('country'))?.long_name || '';
          zip_code = components.find(c => c.types.includes('postal_code'))?.long_name || '';

          const parsed = {
            address_line_1,
            city,
            state,
            country,
            zip_code,
            display_name: place.formatted_address || address_line_1
          };

          setQuery(parsed.address_line_1);
          setLocation({
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          });

          if (onChangeRef.current) onChangeRef.current({ target: { name, value: parsed.address_line_1 } });
          if (onSelectRef.current) onSelectRef.current(parsed);
        });
        
        isInitialized = true;
      }
    };

    loadGoogleMapsScript().then(() => {
      // Small timeout to ensure DOM is ready and Google Maps is fully initialized
      setTimeout(initAutocomplete, 100);
    }).catch(err => {
      console.error("Failed to load Google Maps script", err);
    });

    return () => {
      if (checkInterval) clearInterval(checkInterval);
      if (autocompleteRef.current && window.google && window.google.maps.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [name]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (onChange) onChange(e);
  };

  return (
    <div className="addr-autocomplete" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {label && <label className="addr-label">{label}</label>}

      <div className="addr-input-wrap">
        <input
          ref={inputRef}
          type="text"
          name={name}
          value={query || ''}
          onChange={handleInputChange}
          placeholder={placeholder}
          autoComplete="off"
          disabled={disabled}
          className="addr-input"
        />
        {query && (
          <button
            type="button"
            className="addr-clear"
            onClick={() => {
              setQuery('');
              setLocation(null);
              if (onChange) onChange({ target: { name, value: '' } });
              if (onSelect) onSelect({ address_line_1: '', city: '', state: '', country: '', zip_code: '' });
            }}
            aria-label="Clear"
          >×</button>
        )}
      </div>
    </div>
  );
}

export default AddressAutocomplete;
